import {
  CheckCircleOutline,
  EventNoteOutlined,
  HourglassBottom,
  NotificationsNone,
  TrendingUp,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import React, { FC } from 'react';

interface BookingStatusCardsProps {
  totalBookings: number;
  confirmed: number;
  awaitingConfirmation: number;
  tentative: number;
  waitlisted: number;
  enquiry: number;
  rfq: number;
}

interface StatCardData {
  key: string;
  label: string;
  value: number | string;
  subtitle: string;
  icon: React.ElementType;
  iconColor: string;
  iconBgColor: string;
}

const StatCard: FC<{ stat: StatCardData }> = ({ stat }) => {
  const IconComponent = stat.icon;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        minHeight: 72,
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
      }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: stat.iconBgColor,
          color: stat.iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
        <IconComponent sx={{ fontSize: 22 }} />
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.15 }}>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'text.secondary',
            lineHeight: 1.2,
          }}>
          {stat.label}
        </Typography>
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            color: '#101828',
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
          }}>
          {stat.value}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 400,
            color: 'text.secondary',
            lineHeight: 1.2,
          }}>
          {stat.subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

const BookingStatusCards: FC<BookingStatusCardsProps> = ({
  totalBookings,
  confirmed,
  awaitingConfirmation,
  tentative,
  waitlisted,
  enquiry,
  rfq,
}) => {
  const total = totalBookings || 0;
  const confirmedCount = confirmed || 0;
  const awaitingCount = awaitingConfirmation || 0;
  const tentativeCount = tentative || 0;
  const waitlistedCount = waitlisted || 0;
  const enquiryCount = enquiry || 0;
  const rfqCount = rfq || 0;
  const pendingCount = awaitingCount + tentativeCount + waitlistedCount;
  const openLeadsCount = enquiryCount + rfqCount;
  const confirmedShare = total > 0 ? (confirmedCount / total) * 100 : 0;
  const conversionRate = total > 0 ? (confirmedCount / total) * 100 : 0;

  const stats: StatCardData[] = [
    {
      key: 'total',
      label: 'Total Bookings',
      value: total,
      subtitle: 'All active bookings',
      icon: EventNoteOutlined,
      iconColor: '#1E3A8A',
      iconBgColor: '#E8EEF7',
    },
    {
      key: 'confirmed',
      label: 'Confirmed',
      value: confirmedCount,
      subtitle: `${confirmedShare.toFixed(0)}% of active bookings`,
      icon: CheckCircleOutline,
      iconColor: '#188038',
      iconBgColor: '#E6F4EA',
    },
    {
      key: 'pending',
      label: 'Pending',
      value: pendingCount,
      subtitle: `${awaitingCount} awaiting · ${tentativeCount} tentative · ${waitlistedCount} waitlisted`,
      icon: HourglassBottom,
      iconColor: '#D97706',
      iconBgColor: '#FFFBEB',
    },
    {
      key: 'openLeads',
      label: 'Open Leads',
      value: openLeadsCount,
      subtitle: `${enquiryCount} enquiries · ${rfqCount} RFQs`,
      icon: NotificationsNone,
      iconColor: '#4338CA',
      iconBgColor: '#EEF2FF',
    },
    {
      key: 'conversion',
      label: 'Conversion Rate',
      value: `${conversionRate.toFixed(0)}%`,
      subtitle: `${confirmedCount} of ${total} closed opportunities`,
      icon: TrendingUp,
      iconColor: '#7B1FA2',
      iconBgColor: '#F3E5F5',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(5, 1fr)',
        },
        gap: 1.25,
        width: '100%',
      }}>
      {stats.map(stat => (
        <StatCard key={stat.key} stat={stat} />
      ))}
    </Box>
  );
};

export default BookingStatusCards;
